import { useState, useEffect, useCallback } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
    Activity, ArrowLeft, Building2, CalendarDays, CheckCircle2, Clock, Cpu,
    Download, Eye, ExternalLink, FileText, GitBranch, ImageIcon, Link2, ListChecks, Loader2,
    MessageSquare, Package, User, X, type LucideIcon,
} from "lucide-react"
import { Link, useNavigate } from "@tanstack/react-router"
import { toast } from "react-toastify"
import type { Models } from "appwrite"
import type { ServiceReport } from "@/Wist-blockchain/collections/serviceReport/types"
import { parseAttachment } from "@/Wist-blockchain/collections/serviceReport/types"
import type { Company } from "@/lib/collections/organizations/getOrganizationsFetch"
import type { Instrument } from "@/Wist-blockchain/collections/instruments/types"
import { getReportFilePreviewUrl, getReportFileName } from "../../utils/uploadAnexosStorage"
import { client } from "@/lib/appwrite"
import { AuthImage } from "@/Wist-blockchain/instruments/components/AuthImage"
import { generateServiceReportDoc, type EngineerProfile, type PreviousServiceInfo } from "../../utils/generateServiceReportDoc"

const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "—"
    const datePart = dateStr.split("T")[0]
    return new Date(`${datePart}T12:00:00`).toLocaleDateString("es-EC", {
        day: "2-digit", month: "long", year: "numeric",
    })
}

const formatTime = (iso?: string | null) => {
    if (!iso) return "—"
    return iso.split("T")[1]?.slice(0, 5) ?? "—"
}

const SERVICE_TYPE_LABELS: Record<string, string> = {
    instalacion: "Instalación",
    preventivo: "Preventivo",
    correctivo: "Correctivo",
    "calificacion-validacion": "Calificación / Validación",
    capacitacion: "Capacitación",
    otro: "Otro",
}

const SERVICE_REASON_LABELS: Record<string, string> = {
    instalacion: "Instalación",
    "mantenimiento-programado": "Mantenimiento programado",
    "falla-reportada": "Falla reportada",
    "revision-anomalia": "Revisión por anomalía",
    otro: "Otro",
}

const FINAL_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    operativo: { label: "Operativo", className: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300" },
    operativo_observaciones: { label: "Requiere seguimiento", className: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300" },
    fuera_servicio: { label: "Fuera de servicio", className: "bg-red-100 text-red-600 border-red-200 dark:bg-red-900/40 dark:text-red-300" },
}

// ─── Auth helpers ─────────────────────────────────────────────────────────────

const openAuthFile = async (url: string) => {
    try {
        const headers = (client as unknown as { headers: Record<string, string> }).headers ?? {}
        const res = await fetch(url, { headers })
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        window.open(blobUrl, "_blank")
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
    } catch {
        window.open(url, "_blank")
    }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type SectionColor = "blue" | "purple" | "amber" | "rose" | "green" | "teal" | "orange" | "slate"

const SECTION_COLORS: Record<SectionColor, string> = {
    blue: "bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400",
    purple: "bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400",
    amber: "bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400",
    rose: "bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400",
    green: "bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400",
    teal: "bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400",
    orange: "bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400",
    slate: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400",
}

function SectionTitle({ icon: Icon, title, color = "blue" }: { icon: LucideIcon; title: string; color?: SectionColor }) {
    return (
        <div className="flex items-center gap-2.5 mb-4">
            <div className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${SECTION_COLORS[color]}`}>
                <Icon className="h-4 w-4" />
            </div>
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
        </div>
    )
}

function InfoRow({ label, value }: { label: string; value?: string | null | boolean }) {
    const display = typeof value === "boolean" ? (value ? "Sí" : "No") : (value || "—")
    return (
        <div className="flex justify-between items-start gap-4 py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide shrink-0">{label}</span>
            <span className="text-sm text-gray-800 dark:text-gray-200 text-right font-medium">{display}</span>
        </div>
    )
}

function TagList({ items }: { items: string[] }) {
    if (!items.length) return <p className="text-sm text-gray-400">—</p>
    return (
        <div className="flex flex-wrap gap-1.5 pt-1">
            {items.map((item, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-md bg-gray-100 dark:bg-gray-800 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400 text-[10px] font-mono">{i + 1}.</span>
                    {item}
                </span>
            ))}
        </div>
    )
}

function AnexoItem({ fileId, description }: { fileId: string; description?: string }) {
    const [isDoc, setIsDoc] = useState(false)
    const [fileName, setFileName] = useState<string | null>(null)
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const url = getReportFilePreviewUrl(fileId)

    useEffect(() => {
        if (!isDoc) return
        getReportFileName(fileId).then(setFileName).catch(() => setFileName(null))
    }, [isDoc, fileId])

    const handleClick = useCallback(() => {
        if (isDoc) openAuthFile(url)
        else setLightboxOpen(true)
    }, [isDoc, url])

    return (
        <>
            <div className="flex flex-col gap-1">
                <div
                    onClick={handleClick}
                    className="group relative aspect-square rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-800 hover:border-blue-300 transition-colors cursor-pointer"
                >
                    {isDoc ? (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2 px-3 py-4">
                            <FileText className="h-8 w-8 text-red-400 shrink-0" />
                            <span className="text-[11px] text-gray-500 text-center leading-tight line-clamp-3 w-full wrap-break-word">
                                {fileName ?? "Documento"}
                            </span>
                        </div>
                    ) : (
                        <AuthImage
                            src={url}
                            alt={`Anexo ${fileId}`}
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-200"
                            onError={() => setIsDoc(true)}
                        />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        {isDoc
                            ? <ExternalLink className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            : <Eye className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        }
                    </div>
                </div>
                {description && (
                    <p className="text-[11px] text-muted-foreground text-center wrap-break-word leading-tight px-1">{description}</p>
                )}
            </div>

            {lightboxOpen && !isDoc && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
                    onClick={() => setLightboxOpen(false)}
                >
                    <div className="relative max-w-4xl w-full px-4" onClick={e => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => setLightboxOpen(false)}
                            className="absolute -top-10 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <AuthImage
                            src={url}
                            alt={`Anexo ${fileId}`}
                            className="max-h-[80vh] w-full rounded-xl object-contain"
                        />
                    </div>
                </div>
            )}
        </>
    )
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ServiceReportDetailProps {
    report: ServiceReport
    company?: Company
    instrument?: Instrument
    brandName?: string | null
    activities?: Models.Document[]
    materials?: Models.Document[]
    engineerName?: string
    engineerProfile?: EngineerProfile
    previousServiceInfo?: PreviousServiceInfo
}

// ─── Main component ───────────────────────────────────────────────────────────

const ServiceReportDetail = ({
    report, company, instrument, brandName,
    activities = [], materials = [],
    engineerName = "—", engineerProfile, previousServiceInfo,
}: ServiceReportDetailProps) => {
    const status = FINAL_STATUS_CONFIG[report.finalStatus ?? ""] ?? null
    const navigate = useNavigate()
    const [downloading, setDownloading] = useState(false)

    const handleCreateRevision = () => {
        navigate({
            to: '/instruments/serviceReport/$id/revision/$reportDocId',
            params: { id: report.idInstrument, reportDocId: report.$id },
        })
    }

    const handleDownload = async () => {
        setDownloading(true)
        const result = await generateServiceReportDoc({
            report, company, instrument, brandName, engineerName, engineerProfile, previousServiceInfo,
            activities: activities.map(a => ({
                order: a.order as number, activity: a.activity as string | null,
                result: a.result as string | null, observations: a.observations as string | null,
            })),
            materials: materials.map(m => ({
                order: m.order as number, name: m.name as string | null,
                code: m.code as string | null, quantity: m.quantity as number,
                condition: m.condition as string | null, unitCost: m.unitCost as number,
                ivaPercent: (m.ivaPercent as number) ?? 0,
            })),
        })
        setDownloading(false)
        if (result.success) toast.success("Documento generado correctamente.")
        else toast.error("No se pudo generar el documento. Por favor, inténtalo de nuevo.")
    }

    return (
        <div className="space-y-5 pb-10">

            <Link
                to="/instruments/$id"
                params={{ id: report.idInstrument }}
                className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
                <ArrowLeft className="h-3.5 w-3.5" />
                Volver al equipo
            </Link>

            <div className="rounded-xl border border-blue-100 dark:border-blue-900/40 bg-linear-to-br from-white to-blue-50 dark:from-gray-900 dark:to-blue-950/20 p-5 shadow-sm">
                <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-600 shadow-md shrink-0">
                        <FileText className="h-7 w-7 text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                                {report.idReport}
                            </h1>
                            {report.parentReportId && (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs font-semibold">
                                    REV{report.revisionNumber}
                                </Badge>
                            )}
                            {status && (
                                <Badge className={`border text-xs font-semibold px-3 py-1 ${status.className}`}>
                                    {status.label}
                                </Badge>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Informe de Servicio Técnico</p>
                        {report.parentReportId && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                Revisión de: <span className="font-mono font-medium">{report.parentReportId}</span>
                            </p>
                        )}
                    </div>
                </div>

                {/* stats */}
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500 dark:text-gray-400 border-t border-blue-100 dark:border-blue-900/30 pt-4">
                    <span className="flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 text-blue-400" />
                        {formatDate(report.serviceDate)}
                    </span>
                    <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-blue-400" />
                        {formatTime(report.serviceStartTime)} – {formatTime(report.serviceEndTime)}
                    </span>
                    <span className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-blue-400" />
                        {engineerName}
                    </span>
                    <span className="flex items-center gap-1.5">
                        <Activity className="h-3.5 w-3.5 text-blue-400" />
                        {SERVICE_TYPE_LABELS[report.serviceType ?? ""] ?? report.serviceType ?? "—"}
                    </span>
                </div>

                {/* actions */}
                <div className="mt-4 flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={handleDownload} disabled={downloading}
                        className="cursor-pointer gap-1.5 bg-white dark:bg-gray-800">
                        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                        {downloading ? "Generando..." : "Descargar .docx"}
                    </Button>
                    {report.status === "final" && (
                        <Button size="sm" variant="outline" onClick={handleCreateRevision}
                            className="cursor-pointer gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50 bg-white dark:bg-gray-800">
                            <GitBranch className="h-3.5 w-3.5" />
                            Crear revisión
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

                {/* datos generales */}
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={CalendarDays} title="Datos Generales" color="blue" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <InfoRow label="Tipo de servicio" value={
                            (report.serviceTypes?.length
                                ? report.serviceTypes.map(s => SERVICE_TYPE_LABELS[s] ?? s).join(", ")
                                : SERVICE_TYPE_LABELS[report.serviceType ?? ""] ?? report.serviceType) ?? "—"
                        } />
                        <InfoRow label="Fecha de servicio" value={formatDate(report.serviceDate)} />
                        <InfoRow label="Fecha de informe" value={formatDate(report.reportDate)} />
                        <InfoRow label="Hora entrada" value={formatTime(report.serviceStartTime)} />
                        <InfoRow label="Hora salida" value={formatTime(report.serviceEndTime)} />
                        <InfoRow label="Orden de compra" value={report.purchaseOrder} />
                    </CardContent>
                </Card>

                {/* cliente */}
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={Building2} title="Datos del Cliente" color="purple" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <InfoRow label="Empresa" value={company?.name} />
                        <InfoRow label="RUC" value={company?.ruc} />
                        <InfoRow label="Ciudad" value={company?.city} />
                        <InfoRow label="Dirección" value={company?.address} />
                        <InfoRow label="Contacto" value={report.clientContact} />
                        <InfoRow label="Email contacto" value={report.clientEmail} />
                        <InfoRow label="Teléfono contacto" value={report.clientPhone} />
                        <InfoRow label="Área / Lab" value={report.clientArea} />
                        {/* <InfoRow label="Responsable cierre" value={report.clientResponsible} /> */}
                    </CardContent>
                </Card>

                {/* equipo */}
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={Cpu} title="Identificación del Equipo" color="amber" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <InfoRow label="Equipo" value={instrument?.equipmentName} />
                        <InfoRow label="Marca" value={brandName} />
                        <InfoRow label="Modelo" value={instrument?.model} />
                        <InfoRow label="Serie" value={instrument?.serialNumber} />
                        <InfoRow label="Año fabricación" value={instrument?.manufactureYear ? String(instrument.manufactureYear) : null} />
                        <InfoRow label="Ubicación" value={instrument?.location} />
                        <InfoRow label="Código activo" value={instrument?.clientAssetCode} />
                    </CardContent>
                </Card>

                {/* ingeniero */}
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={User} title="Ingeniero Responsable" color="teal" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <InfoRow label="Nombre" value={engineerName} />
                        <InfoRow label="Cargo" value={engineerProfile?.position} />
                        <InfoRow label="Cédula" value={engineerProfile?.idNumber} />
                        <InfoRow label="Teléfono" value={engineerProfile?.phone} />
                        <InfoRow label="Email" value={engineerProfile?.email} />
                    </CardContent>
                </Card>

                {/* diagnóstico */}
                <Card className="shadow-sm lg:col-span-2">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={Activity} title="Diagnóstico y Resultados" color="rose" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                            <div>
                                <InfoRow label="Motivo" value={SERVICE_REASON_LABELS[report.serviceReason ?? ""] ?? report.serviceReason} />
                                <InfoRow label="Descripción" value={report.reasonDescription} />
                                <InfoRow label="Tipos de calificación" value={
                                    report.qualificationTypes?.length
                                        ? report.qualificationTypes.join(", ")
                                        : report.qualificationType ?? null
                                } />
                                <InfoRow label="Próxima calificación" value={report.nextQualificationDate ? formatDate(report.nextQualificationDate) : null} />
                                <InfoRow label="Estado final" value={FINAL_STATUS_CONFIG[report.finalStatus ?? ""]?.label ?? report.finalStatus} />
                                <InfoRow label="Entregado operativo" value={report.deliveredOperative} />
                            </div>
                            <div>
                                <InfoRow label="Próximo mantenimiento" value={formatDate(report.nextMaintenanceData)} />
                                <InfoRow label="Seguimiento remoto" value={report.requiresRemoteFollow} />
                                <InfoRow label="Primera visita" value={previousServiceInfo?.isFirst} />
                                <InfoRow label="Último servicio" value={previousServiceInfo?.lastDate ? formatDate(previousServiceInfo.lastDate) : null} />
                            </div>
                        </div>

                        {(report.verifiedParameters?.length ?? 0) > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Parámetros verificados</p>
                                <TagList items={report.verifiedParameters ?? []} />
                            </div>
                        )}
                        {(report.recommendations?.length ?? 0) > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Recomendaciones</p>
                                <TagList items={report.recommendations ?? []} />
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* actividades */}
            {activities.length > 0 && (
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={ListChecks} title="Actividades Realizadas" color="green" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                        <th className="text-left py-2 pr-4 text-xs font-medium text-gray-400 uppercase tracking-wide w-8">#</th>
                                        <th className="text-left py-2 pr-4 text-xs font-medium text-gray-400 uppercase tracking-wide">Actividad</th>
                                        <th className="text-left py-2 pr-4 text-xs font-medium text-gray-400 uppercase tracking-wide">Resultado</th>
                                        <th className="text-left py-2 text-xs font-medium text-gray-400 uppercase tracking-wide">Observaciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activities.map((a) => (
                                        <tr key={a.$id} className="border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                                            <td className="py-2.5 pr-4 text-gray-400 font-mono text-xs">{a.order}</td>
                                            <td className="py-2.5 pr-4 text-gray-800 dark:text-gray-200">{(a.activity as string) || "—"}</td>
                                            <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-400">{(a.result as string) || "—"}</td>
                                            <td className="py-2.5 text-gray-500 dark:text-gray-500">{(a.observations as string) || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/*Materiales */}
            {materials.length > 0 && (
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={Package} title="Materiales y Repuestos" color="teal" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100 dark:border-gray-800">
                                        <th className="text-left py-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wide w-8">#</th>
                                        <th className="text-left py-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Nombre</th>
                                        <th className="text-left py-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Código</th>
                                        <th className="text-left py-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Cant.</th>
                                        <th className="text-left py-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Condición</th>
                                        <th className="text-right py-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wide">P. Unit.</th>
                                        <th className="text-right py-2 text-xs font-medium text-gray-400 uppercase tracking-wide">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {materials.map((m) => {
                                        const qty = (m.quantity as number) ?? 0
                                        const cost = (m.unitCost as number) ?? 0
                                        return (
                                            <tr key={m.$id} className="border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                                                <td className="py-2.5 pr-3 text-gray-400 font-mono text-xs">{m.order}</td>
                                                <td className="py-2.5 pr-3 text-gray-800 dark:text-gray-200 font-medium">{(m.name as string) || "—"}</td>
                                                <td className="py-2.5 pr-3 text-gray-500 font-mono text-xs">{(m.code as string) || "—"}</td>
                                                <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-400">{qty}</td>
                                                <td className="py-2.5 pr-3 text-gray-500">{(m.condition as string) || "—"}</td>
                                                <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-400 text-right">${cost.toFixed(2)}</td>
                                                <td className="py-2.5 text-gray-800 dark:text-gray-200 font-semibold text-right">${(qty * cost).toFixed(2)}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t border-gray-200 dark:border-gray-700">
                                        <td colSpan={6} className="pt-3 text-xs font-medium text-gray-400 uppercase tracking-wide text-right pr-3">Total materiales</td>
                                        <td className="pt-3 text-right font-bold text-gray-900 dark:text-white">
                                            ${materials.reduce((acc, m) => acc + ((m.quantity as number) ?? 0) * ((m.unitCost as number) ?? 0), 0).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* observaciones del cliente */}
            {(report.clientObservations?.length ?? 0) > 0 && (
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={MessageSquare} title="Observaciones del Cliente" color="orange" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <div className="space-y-2">
                            {report.clientObservations!.map((obs, i) => (
                                <div key={i} className="flex gap-3 rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30 px-4 py-3">
                                    <span className="text-orange-400 font-mono text-xs shrink-0 mt-0.5">{i + 1}.</span>
                                    <p className="text-sm text-gray-700 dark:text-gray-300">{obs}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/*  Conformidad  */}
            <Card className="shadow-sm">
                <CardHeader className="pb-0 pt-5 px-5">
                    <SectionTitle icon={CheckCircle2} title="Conformidad del Cliente" color="green" />
                </CardHeader>
                <CardContent className="px-5 pb-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                        <InfoRow label="Responsable" value={report.clientResponsible} />
                        <InfoRow label="Cargo" value={report.clientResponsiblePosition} />
                        <InfoRow label="Entregado operativo" value={report.deliveredOperative} />
                    </div>
                </CardContent>
            </Card>

            {/* referencias documentales */}
            {(report.documentReferences?.length ?? 0) > 0 && (
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={Link2} title="Referencias de Documentos" color="slate" />
                    </CardHeader>
                    <CardContent className="px-5 pb-5">
                        <TagList items={report.documentReferences ?? []} />
                    </CardContent>
                </Card>
            )}

            {/* Notas internas */}
            {(report.internalNotes?.length ?? 0) > 0 && (
                <Card className="shadow-sm">
                    <CardHeader className="pb-0 pt-5 px-5">
                        <SectionTitle icon={FileText} title="Notas Internas" color="blue" />
                    </CardHeader>

                    <CardContent className="px-5 pb-5">
                        <div className="rounded-md bg-muted/50 px-4 py-3">
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-6">
                                {report.internalNotes}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/*  anexos */}
            <Card className="shadow-sm">
                <CardHeader className="pb-0 pt-5 px-5">
                    <SectionTitle icon={ImageIcon} title="Anexos" color="slate" />
                </CardHeader>
                <CardContent className="px-5 pb-5">
                    {(report.idAttachments?.length ?? 0) > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {report.idAttachments!.map((raw) => {
                                const { id, description } = parseAttachment(raw)
                                return (
                                    <AnexoItem
                                        key={id}
                                        fileId={id}
                                        description={description}
                                    />
                                )
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400 text-center py-8">Sin anexos adjuntos.</p>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

export default ServiceReportDetail
